/**
 * The Admin dashboard's menus (docs/pdlc/admin-dashboard.md): every page staff
 * open from it, grouped by the job it is for and ordered by how often it is used
 * (production sessions, August to November 2026). The one source for the menus,
 * the tabs of a page that holds several, page headers, and the Overview's search:
 * a page is named, described and gated here once, so no menu offers a page that
 * would refuse the person. Pure.
 *
 * The Surveys menu is its own list (survey-menu.ts) and the Agents pages theirs
 * (agents/pages.ts); both are read in here.
 */
import { AGENTS_FLAG, AGENTS_PAGES } from './agents/pages';
import { canOpen as canOpenSurveyPage, SURVEY_MENU, type SurveyMenuGate } from './survey-menu';

export type AdminGate =
    /** Any internal staff member. */
    | 'internal'
    /** A Studio admin. */
    | 'admin'
    /** Internal staff with a feature flag. */
    | { flag: string }
    /** AI Crosstabs is on for the person (crosstabsMode). */
    | 'aiCrosstabs'
    /** Not production (the feature flag QA board). */
    | 'nonProduction'
    /** May read credit margins (useCreditClaims). */
    | 'marginRead'
    /** A Surveys menu page's own gate. */
    | { survey: SurveyMenuGate };

export interface AdminAccess {
    isInternal: boolean;
    isAdmin: boolean;
    hasFlag: (key: string) => boolean;
    aiCrosstabs: boolean;
    nonProduction: boolean;
    marginRead: boolean;
}

export interface AdminPage {
    key: string;
    label: string;
    /** One line: what the page is for, in the words of the person using it. */
    description: string;
    /** A Lucide icon name (Iconify `lucide:`). */
    icon: string;
    href: string;
    /** Every gate must pass. */
    gate: AdminGate | AdminGate[];
    /** Pages shown as tabs of this one; the menu lists only this page. */
    tabs?: AdminPage[];
    /**
     * Its old names and where it used to be ("Order Forms › License"), so search
     * finds it for someone who remembers the old menus (FORMERLY).
     */
    formerly?: string[];
    /** Pages opened from this one that no menu lists; search finds them (ALSO_FIND). */
    alsoFind?: AdminPage[];
}

export interface AdminSection {
    key: string;
    title: string;
    items: AdminPage[];
}

export interface AdminMenu {
    key: string;
    label: string;
    sections: AdminSection[];
}

const page = (
    key: string,
    label: string,
    description: string,
    icon: string,
    href: string,
    gate: AdminPage['gate'] = 'internal',
    tabs?: AdminPage[],
): AdminPage => ({ key, label, description, icon, href, gate, ...(tabs ? { tabs } : {}) });

const ACCOUNTS: AdminMenu = {
    key: 'accounts',
    label: 'Accounts',
    sections: [
        {
            key: 'find',
            title: 'Find',
            items: [
                page(
                    'users',
                    'Users',
                    'Look up a person: their account, activity and access',
                    'lucide:users',
                    '/tools/reports/users',
                ),
                page(
                    'clients',
                    'Clients',
                    'A client account, its licenses and its dashboards',
                    'lucide:building-2',
                    '/tools/reports/clients',
                    'internal',
                    [
                        page(
                            'licenses',
                            'Licenses',
                            "Each client's licenses and when they end",
                            'lucide:badge-check',
                            '/tools/reports/licenses',
                        ),
                        page(
                            'dashboards',
                            'Dashboards',
                            'The dashboards each client can open',
                            'lucide:layout-dashboard',
                            '/tools/reports/dashboards',
                        ),
                    ],
                ),
                page(
                    'trials',
                    'Trials',
                    'Trials ending, what they did, and their engagement tiers',
                    'lucide:hourglass',
                    '/tools/trial-monitoring',
                    'internal',
                    [
                        page(
                            'engagement-tiers',
                            'Engagement tiers',
                            'Where each engagement tier starts',
                            'lucide:sliders-horizontal',
                            '/tools/engagement-config',
                        ),
                        page(
                            'freemium-usage',
                            'Freemium usage',
                            'Free DANI use by address',
                            'lucide:globe',
                            '/tools/freemium-usage',
                            'admin',
                        ),
                    ],
                ),
                page(
                    'dani-access',
                    'DANI access',
                    'Who has DANI, and of which kind',
                    'lucide:key-round',
                    '/tools/dani-access',
                ),
            ],
        },
        {
            key: 'client-actions',
            title: 'Client actions',
            items: [
                page(
                    'license-form',
                    'Set up a license',
                    'A new license for a client',
                    'lucide:file-badge',
                    '/tools/forms/license',
                ),
                page(
                    'deposit-form',
                    'Add a deposit',
                    "Money paid in on a client's account",
                    'lucide:circle-dollar-sign',
                    '/tools/forms/deposit',
                ),
                page(
                    'group-membership',
                    'Group membership',
                    'Who belongs to which user group',
                    'lucide:users-round',
                    '/tools/forms/group-management',
                    'admin',
                ),
                page(
                    'vtracker-form',
                    'Release a vTracker',
                    'A new vTracker+ for a client',
                    'lucide:chart-line',
                    '/tools/forms/vtracker',
                ),
            ],
        },
    ],
};

const CREDITS: AdminMenu = {
    key: 'credits',
    label: 'Credits',
    sections: [
        {
            key: 'credits',
            title: 'Credits',
            items: [
                page(
                    'credits-fleet',
                    'Fleet',
                    "Every client's balance, burn and pool",
                    'lucide:coins',
                    '/tools/credits',
                ),
                page(
                    'rate-card',
                    'Rate card',
                    'Credits per unit, by version',
                    'lucide:receipt-text',
                    '/tools/credits/rate-card',
                ),
                page(
                    'margins',
                    'Margins',
                    'Revenue against the cost of goods',
                    'lucide:percent',
                    '/tools/credits/margins',
                    'marginRead',
                ),
                page(
                    'cogs',
                    'Cost of goods',
                    'What each unit costs to deliver',
                    'lucide:package',
                    '/tools/credits/cogs',
                    'marginRead',
                ),
                page(
                    'cost-dashboard',
                    'Cost dashboard',
                    'Cloud and model spend',
                    'lucide:chart-column',
                    '/tools/cost-dashboard',
                    // Every staff member, for now (2026-11-02); the margin pages still need the claim.
                    'internal',
                ),
            ],
        },
    ],
};

const SURVEYS: AdminMenu = {
    key: 'surveys',
    label: 'Surveys',
    sections: SURVEY_MENU.map((s) => ({
        key: s.key,
        title: s.title,
        items: s.items.map((i) => ({ ...i, gate: { survey: i.gate } })),
    })),
};

const DANI: AdminMenu = {
    key: 'dani',
    label: 'DANI',
    sections: [
        {
            key: 'watch',
            title: 'Watch',
            items: [
                page(
                    'chat-history',
                    'Chat history',
                    'Conversations, their feedback and the messages staff marked',
                    'lucide:messages-square',
                    '/tools/reports/ai-chat-history',
                    'internal',
                    [
                        page(
                            'marked-messages',
                            'Marked messages',
                            'Messages staff marked, to review or export',
                            'lucide:bookmark',
                            '/tools/reports/marked-messages',
                        ),
                    ],
                ),
                page(
                    'dani-metrics',
                    'Metrics',
                    'DANI use over time',
                    'lucide:chart-spline',
                    '/tools/metrics/dani',
                ),
                page(
                    'feature-adoption',
                    'Feature adoption',
                    'What clients and staff did in each Studio tool',
                    'lucide:chart-pie',
                    '/tools/adoption',
                ),
            ],
        },
        {
            key: 'shape',
            title: 'Shape',
            items: [
                page(
                    'plan-templates',
                    'Plan templates',
                    'The plans DANI builds reports from, and how they look',
                    'lucide:layout-template',
                    '/tools/plan-templates',
                    'admin',
                    [
                        page(
                            'plan-template-styles',
                            'Styles',
                            'How a plan report is styled',
                            'lucide:palette',
                            '/tools/plan-template-styles',
                            'admin',
                        ),
                        page(
                            'report-templates',
                            'Report templates',
                            'The report templates crosstab builds use',
                            'lucide:file-spreadsheet',
                            '/tools/banner-plan-templates',
                            'admin',
                        ),
                    ],
                ),
                page(
                    'ai-configs',
                    'AI configs',
                    'Which DANI agent each audience uses',
                    'lucide:settings-2',
                    '/tools/ai-configs',
                    'admin',
                ),
                page(
                    'insights-mining',
                    'Insights mining',
                    'Mine DANI conversations into Spotlight',
                    'lucide:pickaxe',
                    '/tools/discover-mine',
                    'admin',
                ),
                page(
                    'studio-release-notes',
                    'Studio release notes',
                    'What Studio users see as new',
                    'lucide:megaphone',
                    '/tools/reports/release-notes',
                ),
            ],
        },
    ],
};

const GROWTH: AdminMenu = {
    key: 'growth',
    label: 'Growth',
    sections: [
        {
            key: 'growth',
            title: 'Growth',
            items: [
                page(
                    'trial-emails',
                    'Trial emails',
                    'The four trial emails, their links and their DANI questions',
                    'lucide:mail',
                    '/tools/trial-onboarding',
                    'internal',
                    [
                        page(
                            'onboarding-emails',
                            'Email review',
                            "Each category's email copy and links",
                            'lucide:mail-check',
                            '/tools/onboarding-emails',
                        ),
                    ],
                ),
                page(
                    'insight-engine',
                    'Insight engine',
                    'Prospecting and engagement insights for a client',
                    'lucide:sparkles',
                    '/dani/insight-engine',
                ),
                page(
                    'studio-catalog',
                    'Studio Catalog',
                    "MFour's data, its quality and its coverage",
                    'lucide:database',
                    '/ai/studio-catalog',
                ),
            ],
        },
    ],
};

const AGENT_WORK = new Set(['agents_chat', 'agents_jobs', 'agents_ooh']);
const agentPage = (p: (typeof AGENTS_PAGES)[number]): AdminPage =>
    page(p.key, p.label, p.description, AGENT_ICONS[p.key] ?? 'lucide:bot', p.href, {
        flag: AGENTS_FLAG,
    });
const AGENT_ICONS: Record<string, string> = {
    agents_chat: 'lucide:message-square-text',
    agents_jobs: 'lucide:calendar-clock',
    agents_usage: 'lucide:chart-bar',
    agents_ooh: 'lucide:map',
    agents_skills: 'lucide:zap',
    agents_memories: 'lucide:brain',
    agents_models: 'lucide:cpu',
    agents_release_notes: 'lucide:megaphone',
};

const AGENTS: AdminMenu = {
    key: 'agents',
    label: 'Agents',
    sections: [
        {
            key: 'work',
            title: 'Work',
            items: AGENTS_PAGES.filter((p) => AGENT_WORK.has(p.key)).map(agentPage),
        },
        {
            key: 'run',
            title: 'Run the agents',
            items: AGENTS_PAGES.filter((p) => !AGENT_WORK.has(p.key)).map(agentPage),
        },
    ],
};

const CROSSTAB_GATE: AdminGate[] = ['admin', 'aiCrosstabs'];

const SETTINGS: AdminMenu = {
    key: 'settings',
    label: 'Settings',
    sections: [
        {
            key: 'settings',
            title: 'Settings',
            items: [
                page(
                    'feature-flags',
                    'Feature flags',
                    'Flags and their overrides',
                    'lucide:toggle-right',
                    '/tools/feature-flags',
                    'admin',
                    [
                        page(
                            'feature-flags-qa',
                            'QA',
                            'Check that each flag gates what it should',
                            'lucide:list-checks',
                            '/tools/feature-flags/qa',
                            ['admin', 'nonProduction'],
                        ),
                    ],
                ),
                page(
                    'crosstab-settings',
                    'Crosstab settings',
                    'Starting setups, privacy, display, weighting, exports and DANI',
                    'lucide:table-2',
                    '/tools/crosstab-setups',
                    CROSSTAB_GATE,
                    [
                        page(
                            'crosstab-privacy',
                            'Privacy',
                            "An account's minimum group size",
                            'lucide:shield',
                            '/tools/crosstab-privacy',
                            CROSSTAB_GATE,
                        ),
                        page(
                            'crosstab-display-defaults',
                            'Display defaults',
                            "How an account's crosstabs look until each person chooses",
                            'lucide:eye',
                            '/tools/crosstab-display-defaults',
                            CROSSTAB_GATE,
                        ),
                        page(
                            'crosstab-weight-targets',
                            'Weight targets',
                            'Census and BLS targets and their limits',
                            'lucide:scale',
                            '/tools/crosstab-weight-targets',
                            CROSSTAB_GATE,
                        ),
                        page(
                            'crosstab-export-presets',
                            'Export presets',
                            'How a delivered workbook is laid out',
                            'lucide:file-down',
                            '/tools/crosstab-export-presets',
                            CROSSTAB_GATE,
                        ),
                        page(
                            'crosstabs-dani',
                            'DANI in Crosstabs',
                            'Switch DANI off for an account or project',
                            'lucide:bot-off',
                            '/tools/crosstabs-dani',
                            CROSSTAB_GATE,
                        ),
                    ],
                ),
                page(
                    'data-catalog-refresh',
                    'Data catalog refresh',
                    'Run or review the catalog cache job',
                    'lucide:refresh-cw',
                    '/tools/data-catalog-populate',
                    'admin',
                ),
                page(
                    'dev-portal',
                    'Developer portal',
                    'The API that Builder, the agents and outside clients publish through',
                    'lucide:code-xml',
                    '/tools/dev-portal',
                    { flag: 'dev-portal' },
                ),
            ],
        },
    ],
};

/**
 * Where each page used to be (the menus before November 2026) and the other
 * names people call it by, keyed by page. Search reads these, so a page that
 * moved or was renamed is still found by what someone remembers.
 */
const FORMERLY: Record<string, string[]> = {
    users: ['Reports › Users', 'people', 'staff'],
    clients: ['Reports › Clients', 'accounts', 'orgs', 'organizations'],
    licenses: ['Reports › Licenses', 'entitlements'],
    dashboards: ['Reports › Dashboards'],
    trials: ['DANI › Trial Monitoring', 'trial monitoring'],
    'engagement-tiers': ['DANI › Engagement Config', 'engagement config'],
    'freemium-usage': ['DANI › Freemium Usage', 'freemium', 'IP usage'],
    'dani-access': ['DANI › DANI Access'],
    'license-form': ['Order Forms › License', 'entitlement form', 'new license'],
    'deposit-form': ['Order Forms › Deposit', 'deposit'],
    'group-membership': ['Order Forms › Group Management', 'group management'],
    'vtracker-form': ['Order Forms › Release New vTracker+', 'vtracker'],
    'credits-fleet': ['Credits › Organizations', 'balances', 'ledger'],
    'rate-card': ['Credits › Rate Card'],
    margins: ['Credits › Margins'],
    cogs: ['Credits › Cost of goods', 'COGS'],
    'cost-dashboard': ['Credits › Cost Dashboard', 'AWS cost', 'spend'],
    'chat-history': ['DANI › Chat History', 'conversations', 'feedback'],
    'marked-messages': ['DANI › Marked Messages Report'],
    'dani-metrics': ['DANI › Metrics', 'usage'],
    'feature-adoption': ['DANI › Feature Adoption', 'adoption'],
    'plan-templates': ['DANI › Plan Templates'],
    'plan-template-styles': ['DANI › Plan Template Styles', 'markdown styling'],
    'report-templates': [
        'DANI › Banner Plan Templates',
        'Platform › AI Report Templates',
        'banner plan',
    ],
    'ai-configs': ['DANI › Configs', 'DANI config'],
    'insights-mining': ['Platform › Mine Insights', 'mine insights', 'Spotlight', 'harvest'],
    'studio-release-notes': ['DANI › Release Notes'],
    'trial-emails': ['DANI › Trial Onboarding', 'trial onboarding', 'HubSpot'],
    'onboarding-emails': ['DANI › Onboarding Emails', 'onboarding emails'],
    'insight-engine': ['DANI › Insight Engine', 'prospecting'],
    'studio-catalog': ['DANI › Studio Catalog', 'data catalog'],
    'feature-flags': ['Platform › Feature Flags', 'flags', 'overrides'],
    'feature-flags-qa': ['Reports › Feature flags QA'],
    'crosstab-settings': ['Platform › Crosstab Setups', 'crosstab setups', 'study setups'],
    'crosstab-privacy': ['Platform › Crosstab Privacy', 'minimum group size'],
    'crosstab-display-defaults': ['Platform › Crosstab Display Defaults'],
    'crosstab-weight-targets': ['Platform › Crosstab Weight Targets', 'weighting'],
    'crosstab-export-presets': ['Platform › Crosstab Export Presets', 'workbook'],
    'crosstabs-dani': ['Platform › DANI in Crosstabs'],
    'data-catalog-refresh': ['Platform › Data Catalog Populate', 'data catalog populate'],
    'dev-portal': ['Surveys › Reference › Developer portal', 'API', 'docs'],
    'legacy-feasibility': ['Feasibility', 'feasibility tool'],
    'reference-data': ['recruiters', 'survey groups'],
    'fielding-console': ['pause', 'resume', 'close survey'],
    scrubber: ['scrub', 'reject', 'retire'],
};

const searchOnly = (
    key: string,
    label: string,
    description: string,
    icon: string,
    href: string,
    gate: AdminPage['gate'],
): AdminPage => page(key, label, description, icon, href, gate);

/**
 * Pages opened from another page that no menu lists, keyed by the page they open
 * from. Search finds them; the menus never show them.
 */
const ALSO_FIND: Record<string, AdminPage[]> = {
    'credits-fleet': [
        searchOnly(
            'ledger-shadow',
            'Shadow report',
            'Old and new credit balances side by side',
            'lucide:columns-2',
            '/tools/credits/ledger-shadow',
            'marginRead',
        ),
    ],
    'fielding-console': [
        searchOnly(
            'fielding-history',
            'Fielding history',
            'Every job, push run, pause, resume and close, with who made it',
            'lucide:history',
            '/tools/staff-fielding/history',
            { survey: { staffTool: 'fielding-console' } },
        ),
    ],
    'feasibility-rates': [
        searchOnly(
            'feasibility-weekly-report',
            'Feasibility weekly report',
            'What the latest fielding says about each rate, and what to decide',
            'lucide:clipboard-list',
            '/tools/feasibility-rates/report',
            { flag: 'feasibility-dani' },
        ),
    ],
    agents_chat: [
        searchOnly(
            'agents-chat-history',
            'Agents chat history',
            'Every agents chat, searchable',
            'lucide:history',
            '/tools/agents/chat/history',
            { flag: AGENTS_FLAG },
        ),
    ],
};

function withSearchWords(p: AdminPage): AdminPage {
    return {
        ...p,
        ...(FORMERLY[p.key] ? { formerly: FORMERLY[p.key] } : {}),
        ...(ALSO_FIND[p.key] ? { alsoFind: ALSO_FIND[p.key]!.map(withSearchWords) } : {}),
        ...(p.tabs ? { tabs: p.tabs.map(withSearchWords) } : {}),
    };
}

/** In nav order: by how often each is used, then the order work happens. */
export const ADMIN_MENUS: readonly AdminMenu[] = [
    ACCOUNTS,
    CREDITS,
    SURVEYS,
    DANI,
    GROWTH,
    AGENTS,
    SETTINGS,
].map((m) => ({
    ...m,
    sections: m.sections.map((s) => ({ ...s, items: s.items.map(withSearchWords) })),
}));

function passes(gate: AdminGate, access: AdminAccess): boolean {
    if (!access.isInternal) return false;
    if (gate === 'internal') return true;
    if (gate === 'admin') return access.isAdmin;
    if (gate === 'aiCrosstabs') return access.aiCrosstabs;
    if (gate === 'nonProduction') return access.nonProduction;
    if (gate === 'marginRead') return access.marginRead;
    if ('flag' in gate) return access.hasFlag(gate.flag);
    return canOpenSurveyPage(gate.survey, access);
}

export function canOpenPage(p: Pick<AdminPage, 'gate'>, access: AdminAccess): boolean {
    return (Array.isArray(p.gate) ? p.gate : [p.gate]).every((g) => passes(g, access));
}

/** The menus as this person may use them: a page, section or menu with nothing to open is left out. */
export function adminMenusFor(access: AdminAccess): AdminMenu[] {
    return ADMIN_MENUS.map((m) => ({
        ...m,
        sections: m.sections
            .map((s) => ({
                ...s,
                items: s.items
                    .map((i) => ({
                        ...i,
                        ...(i.tabs ? { tabs: i.tabs.filter((t) => canOpenPage(t, access)) } : {}),
                        ...(i.alsoFind
                            ? { alsoFind: i.alsoFind.filter((t) => canOpenPage(t, access)) }
                            : {}),
                    }))
                    .filter((i) => canOpenPage(i, access)),
            }))
            .filter((s) => s.items.length > 0),
    })).filter((m) => m.sections.length > 0);
}

/** Every page as a flat list, tabs included, with the menu and section it sits in. */
export function allAdminPages(menus: readonly AdminMenu[] = ADMIN_MENUS): {
    menu: AdminMenu;
    section: AdminSection;
    item: AdminPage;
    parent: AdminPage | null;
    /** In the menu, a tab of a page, or found only by search. */
    kind: 'menu' | 'tab' | 'search';
}[] {
    return menus.flatMap((menu) =>
        menu.sections.flatMap((section) =>
            section.items.flatMap((item) => [
                { menu, section, item, parent: null, kind: 'menu' as const },
                ...(item.tabs ?? []).map((t) => ({
                    menu,
                    section,
                    item: t,
                    parent: item,
                    kind: 'tab' as const,
                })),
                ...(item.alsoFind ?? []).map((t) => ({
                    menu,
                    section,
                    item: t,
                    parent: item,
                    kind: 'search' as const,
                })),
            ]),
        ),
    );
}

const within = (pathname: string, href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

/**
 * The page a path belongs to (a detail page counts as its list), and, for a page
 * that holds tabs or is one, the tab set. The longest matching href wins, so
 * /tools/feature-flags/qa is the QA tab, not Feature flags.
 */
export function adminPageOf(
    pathname: string,
    menus: readonly AdminMenu[] = ADMIN_MENUS,
): {
    menu: AdminMenu;
    section: AdminSection;
    item: AdminPage;
    /** The page and its tabs, when it has or is one; null otherwise. */
    tabSet: AdminPage[] | null;
} | null {
    const hits = allAdminPages(menus)
        .filter((p) => within(pathname, p.item.href))
        .sort((a, b) => b.item.href.length - a.item.href.length);
    const hit = hits[0];
    if (!hit) return null;
    // A page found only by search is opened from its parent, but is not one of its tabs.
    const owner = hit.kind === 'tab' ? hit.parent! : hit.item;
    const tabSet = owner.tabs?.length ? [owner, ...owner.tabs] : null;
    return { menu: hit.menu, section: hit.section, item: hit.item, tabSet };
}
